/* oxlint-disable typescript-eslint/prefer-for-of -- this module ships in the CloudFront Functions edge bundle, and cloudfront-js-2.0 rejects `for-of` (`SyntaxError: Token "of" not supported`). Verified via aws cloudfront test-function probes. */
/**
 * `lis_bucket` cookie: per-user opaque ID owned by the analytics layer.
 * Persistent (1y), read by the edge function (or the lambda fallback) for
 * deterministic experiment bucketing. NOT a security token — entropy is
 * `Math.random()`-based and acceptable only for bucket-distribution.
 */
export const LIS_BUCKET_COOKIE_NAME = 'lis_bucket';

/**
 * Request header set unconditionally by the CloudFront viewer-request function
 * to carry the bucket id to the SSR lambda. Its mere presence — separate from
 * the `x-variants` value — is the "edge function ran" signal: the lambda must
 * NOT fall back to its own bucket-minting path when this is set, even if
 * `x-variants` is empty (which happens for non-enrolled users).
 */
export const LIS_BUCKET_HEADER_NAME = 'x-lis-bucket';

/**
 * Request header set by viewer-request only when a fresh `lis_bucket` was
 * minted on this request. The viewer-response handler reads it to decide
 * whether to emit `Set-Cookie` for `lis_bucket` on the browser response.
 */
export const LIS_BUCKET_NEW_HEADER_NAME = 'x-lis-bucket-new';

const ONE_YEAR_SECONDS = 365 * 24 * 60 * 60;

/**
 * Validate a `lis_bucket` cookie value. Trusted shape is the output of
 * `generateBucketId`: non-empty lowercase base36, ≤32 chars.
 *
 * The cookie value is viewer-controlled. Without this guard an attacker
 * (or a stray browser extension) can send an empty / oversized / non-ASCII
 * value, which then feeds into bucket-distribution math and the
 * `x-lis-bucket` header forwarded to origin. A million viewers all sending
 * `lis_bucket=AAA` would collapse the experiment into one variant.
 */
export function isValidBucketId(value: string): boolean {
  return /^[0-9a-z]{1,32}$/.test(value);
}

/**
 * Read `lis_bucket` from a `Cookie` header. Returns undefined if absent
 * OR if the value fails `isValidBucketId` — callers treat that the same
 * as "no cookie" and mint a fresh one.
 *
 * Accepts the raw `Cookie` header value (`name1=val1; name2=val2`).
 */
export function parseLisBucketCookie(
  cookieHeader: string | null | undefined,
): string | undefined {
  if (!cookieHeader) return undefined;
  // Classic indexed for-loop: this function ships in the CloudFront
  // Functions edge bundle, and cloudfront-js-2.0 rejects `for-of` with
  // `SyntaxError: Token "of" not supported`. The `pair === undefined`
  // check is to satisfy `noUncheckedIndexedAccess`; unreachable in practice.
  const pairs = cookieHeader.split(';');
  for (let i = 0; i < pairs.length; i++) {
    const pair = pairs[i];
    if (pair === undefined) break;
    const trimmed = pair.trim();
    const eq = trimmed.indexOf('=');
    if (eq === -1) continue;
    if (trimmed.slice(0, eq) === LIS_BUCKET_COOKIE_NAME) {
      const value = trimmed.slice(eq + 1);
      return isValidBucketId(value) ? value : undefined;
    }
  }
  return undefined;
}

/**
 * Produce a `Set-Cookie` header value for a new or refreshed bucket id.
 * Caller emits this on the response (lambda) or via the viewer-response
 * CloudFront Function on cache hits.
 */
export function serializeLisBucketCookie(bucketId: string): string {
  return `${LIS_BUCKET_COOKIE_NAME}=${bucketId}; Path=/; Max-Age=${ONE_YEAR_SECONDS}; SameSite=Lax; Secure`;
}

/**
 * Mint a new bucket id. ~96 bits of entropy via three Math.random rounds
 * (each yields ~11 base36 chars), capped at 32 chars. Edge-runtime-safe
 * (no Node crypto); loop shape minifies smaller for the edge byte budget.
 */
export function generateBucketId(): string {
  let id = '';
  while (id.length < 32) id += Math.random().toString(36).slice(2);
  return id.slice(0, 32);
}
